import { Grid } from '@material-ui/core';
import dateFormat from 'dateformat';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import EmeraldModal from 'src/common/components/emerald-modal/emerald-modal';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import ThemeLogo from 'src/common/components/theme-logo/theme-logo';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { AppState } from 'src/common/redux/configure-store';
import { POSModeActions } from 'src/common/redux/reducers/pos-mode-reducer';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { signOff } from 'src/login/business-logic/sign-off';
import SizedGridItem from 'src/sized-grid-item/sized-grid-item';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { IMenuPanel } from '../../interfaces/IMenuPanel';
import HomeMenu from '../home-menu';
import LoginMenu from '../login-menu';
import TrainingLogoutButtons from '../training-logout-buttons';
import { TRAINING_MODE_LOGOUT_MODAL } from '../training-logout-buttons/training-logout-buttons';
import { styles } from './styles';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { useAuthContext } from 'src/auth/auth-context';

export const LOGOUT_MODAL = 'logout-modal';

const MenuPanel: React.FC<IMenuPanel> = ({
  isOpened,
  menuMode,
  displayName,
  touchPointId,
  toggleMenuDisplay,
  isActiveTransaction,
  trainingModeActive,
  toggleTrainingMode,
  registerModeActive,
}): JSX.Element => {
  const configuration = getCurrentXLRConfig();

  const classes = styles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const { logout } = useAuthContext();

  const getCurrentDate = (): string => {
    return dateFormat(new Date(), 'ddd d mmm h:MM TT');
  };

  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  // TODO: This is re-rendering too often. Move to its own component and memoize
  const [time, setTime] = useState<string>(getCurrentDate());

  useEffect((): void => {
    setModalOpened(isOpened);
  }, [isOpened]);

  useEffect((): (() => void) => {
    const interval = setInterval(() => setTime(getCurrentDate()), 1000);

    return function cleanup() {
      clearInterval(interval);
    };
  });
  const renderMenu = (menuType: 'home' | 'login'): JSX.Element | null => {
    switch (menuType) {
      case 'home':
        return (
          <HomeMenu
            trainingModeActive={trainingModeActive}
            displayName={displayName}
            touchPointId={touchPointId}
            time={time}
            toggleMenuDisplay={toggleMenuDisplay}
            toggleTrainingMode={toggleTrainingMode}
            handleLogout={handleLogout}
            isActiveTransaction={isActiveTransaction}
          />
        );
      case 'login':
        return (
          <LoginMenu
            trainingModeActive={trainingModeActive}
            toggleMenuDisplay={toggleMenuDisplay}
            toggleTrainingMode={toggleTrainingMode}
            registerModeActive={registerModeActive}
          />
        );
    }
  };

  const handleLogout = (): void => {
    if (trainingModeActive) {
      showTrainingModeLogoutMessage();
    } else {
      showLogoutMessage();
    }
  };

  const showTrainingModeLogoutMessage = (): void => {
    const message: IMessageModal = {
      message: 'ExitTrainingMode',
      type: EmeraldDialogTypes.Info,
      subMessage: 'TrainingModeLogoutDescription',
      onAccept: () => {},
      onCancel: () => {},
      acceptButtonLabel: '',
      closeButtonLabel: '',
      buttonComponent: <TrainingLogoutButtons trainingModeActive={trainingModeActive} />,
      size: MessageModalSizeEnum.Large,
    };
    showEmeraldDialog({ ...message, modalId: TRAINING_MODE_LOGOUT_MODAL });
  };

  const showLogoutMessage = (): void => {
    const message: IMessageModal = {
      message: 'SignOffCommand',
      type: EmeraldDialogTypes.InfoDecision,
      subMessage: 'Sign_Off_Confirmation',
      onAccept: () => {
        signOff(trainingModeActive, undefined, logout);
      },
      onCancel: () => {
        popEmeraldModal(LOGOUT_MODAL);
      },
      acceptButtonLabel: 'Yes',
      closeButtonLabel: 'No',
      size: MessageModalSizeEnum.Medium,
    };
    showEmeraldDialog({ ...message, modalId: LOGOUT_MODAL });
  };

  return (
    <EmeraldModal open={isModalOpened}>
      <Grid
        container
        direction='column'
        className={`${classes.background} fullHeight`}
        wrap='nowrap'
      >
        {trainingModeActive && (
          <TrainingModeWatermark
            dataTestId='menu-panel-training-mode-watermark'
            supplementalStyles={trainingModeClasses.menuPanelTrainingWaterMark}
          />
        )}
        <SizedGridItem size={'9.89vh'} className={` ${classes.logoArea} fullWidth`}>
          <Grid
            container
            className={`fullHeight`}
            style={{
              paddingTop: '2.12vh',
              paddingBottom: '2.3vh',
            }}
          >
            <Grid item xs={6} className={` fullWidth fullHeight`}>
              <Grid container direction='column' justifyContent='center' className={`fullHeight`}>
                <Grid item xs={'auto'}>
                  <Grid item xs={12}>
                    <ThemeLogo styles={classes.storeLogo} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} className={`fullHeight`}>
              <Grid
                container
                direction='column'
                justifyContent='center'
                alignContent='flex-end'
                className={`fullHeight`}
              >
                <Grid container className={classes.emeraldContainer}>
                  <Grid item xs={12} className={`emerald ${classes.emeraldText}`}>
                    {t(configuration.productName)}
                  </Grid>
                  <Grid item xs={12} className={`emerald ${classes.xlrText}`}>
                    {t('XLRPOS')}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </SizedGridItem>
        {renderMenu(menuMode)}
      </Grid>
    </EmeraldModal>
  );
};

const mapStateToProps = (state: AppState): { trainingModeActive: boolean } => ({
  trainingModeActive: state.posMode.trainingMode,
});
const mapDispatchToProps = {
  toggleTrainingMode: POSModeActions.toggleTrainingMode,
};
export default connect(mapStateToProps, mapDispatchToProps)(MenuPanel);
